// @flow
import React, { PureComponent, type ComponentType } from "react";
import { connect, Dispatch } from "react-redux";
import Split from "split.js";
import panesDefaultState from "data/panes";
import { getDisplayName } from "data/constants";
import { setSplitPanesAction, persistSplitPanesAction } from "data/actions";
import isEqual from "lodash/isEqual";
import * as s from "data/reducers/selectors";
// import getToggles from "data/toggles";

type Props = {
    viewName?: string,
    // from redux
    dispatch: Dispatch,
    selectedToggles: Array<string>,
    panes: Object,
    // from TogglesHandler
    setToggles: Function,
};

type Config = {
    viewName?: string,
    paneIds: Array<string>,
    toggleIds: Array<string>,
    direction?: string,
};

const SplitPanes = ({
    viewName,
    paneIds,
    toggleIds,
    direction = "vertical",
}: Config) => (WrappedComponent: ComponentType<any>) => {
    class PP extends PureComponent<Props> {
        split1: Split;
        split2: Split;

        static displayName = `SplitPanes(${getDisplayName(WrappedComponent)})`;

        componentDidMount() {
            let paneIdx = 0;
            this.split1 = Split([paneIds[paneIdx++], paneIds[paneIdx++]], {
                sizes: this.props.panes[0],
                direction:
                    paneIds.length === 2
                        ? direction /*"vertical"*/
                        : "horizontal",
                minSize: 0,
                gutterSize: 10,
                onDragEnd: () => {
                    // adjust toggles based on panes' dimensions
                    const dimensions = this.split1.getSizes();
                    // const toggles = getToggles(viewName || this.props.viewName);
                    // let toggleUpdated = false;
                    // for (let i = 0; i < dimensions.length; i++) {
                    //     const isSelected = dimensions[i] >= 1;
                    //     if (toggles.toggles[i].isSelected !== isSelected) {
                    //         toggles.toggles[i].isSelected = isSelected;
                    //         toggleUpdated = true;
                    //     }
                    // }
                    // /* istanbul ignore else */
                    // if (toggleUpdated && toggles != null) {
                    //     this.props.setToggles(toggles);
                    // }

                    this.dispatch({
                        persist: true,
                        view:
                            viewName ||
                            /* istanbul ignore next */ this.props.viewName,
                        name: 0,
                        dimensions,
                    });
                },

                elementStyle: /* istanbul ignore next */ (
                    dimension,
                    size,
                    gutterSize
                ) => {
                    return {
                        "flex-basis":
                            "calc(" + size + "% - " + gutterSize + "px)",
                    };
                },
                gutterStyle: /* istanbul ignore next */ (
                    dimension,
                    gutterSize
                ) => {
                    return { "flex-basis": gutterSize + "px" };
                },
            });

            if (paneIdx < paneIds.length) {
                this.split2 = Split([paneIds[paneIdx++], paneIds[paneIdx++]], {
                    sizes: this.props.panes[1],
                    minSize: 0,
                    gutterSize: 10,
                    direction: "vertical",
                    onDragEnd: () => {
                        this.dispatch({
                            persist: true,
                            view:
                                viewName ||
                                /* istanbul ignore next */ this.props.viewName,
                            name: 1,
                            dimensions: this.split2.getSizes(),
                        });
                    },
                    elementStyle: /* istanbul ignore next */ (
                        dimension,
                        size,
                        gutterSize
                    ) => {
                        return {
                            "flex-basis":
                                "calc(" + size + "% - " + gutterSize + "px)",
                        };
                    },
                    gutterStyle: /* istanbul ignore next */ (
                        dimension,
                        gutterSize
                    ) => {
                        return { "flex-basis": gutterSize + "px" };
                    },
                });
            }
        }

        componentDidUpdate(prevProps: Props) {
            if (paneIds.length === 2) {
                /* istanbul ignore next */
                if (
                    !isEqual(
                        prevProps.selectedToggles,
                        this.props.selectedToggles
                    )
                ) {
                    const selected = this.props.selectedToggles;

                    let master = panesDefaultState[
                        viewName || this.props.viewName
                    ][0].slice(0);
                    if (!selected.includes(toggleIds[0])) {
                        master = [0, 100];
                    } else if (!selected.includes(toggleIds[1])) {
                        master = [100, 0];
                    }

                    // document.getElementsByClassName(
                    //     "gutter-horizontal"
                    // )[0].style.display =
                    //     master[0] === 0 || master[0] === 100 ? "none" : "inline";
                    this.split1.setSizes(master);
                    this.dispatch({
                        persist: prevProps.selectedToggles != null, //Do not persist initial set of toggles
                        view:
                            viewName ||
                            /* istanbul ignore next */ this.props.viewName,
                        name: 0,
                        dimensions: master,
                    });
                }

                /* istanbul ignore next */
                if (prevProps.panes[0] !== this.props.panes[0]) {
                    this.split1.setSizes(this.props.panes[0]);
                }
            } else {
                /* istanbul ignore next */
                if (
                    !isEqual(
                        prevProps.selectedToggles,
                        this.props.selectedToggles
                    )
                ) {
                    const selected = this.props.selectedToggles;

                    // let master =
                    //     prevProps.panes && prevProps.panes[0]
                    //         ? prevProps.panes[0].slice(0)
                    //         : panesDefaultState[
                    //               viewName || this.props.viewName
                    //           ][0].slice(0);
                    let master = panesDefaultState[
                        viewName ||
                            /* istanbul ignore next */ this.props.viewName
                    ][0].slice(0);

                    if (!selected.includes(toggleIds[0])) {
                        master = [0, 100];
                    }

                    // let slave =
                    //     prevProps.panes && prevProps.panes[1]
                    //         ? prevProps.panes[1].slice(0)
                    //         : panesDefaultState[
                    //               viewName || this.props.viewName
                    //           ][1].slice(0);
                    let slave = panesDefaultState[
                        viewName ||
                            /* istanbul ignore next */ this.props.viewName
                    ][1].slice(0);
                    if (!selected.includes(toggleIds[1])) slave[0] = 0;
                    if (!selected.includes(toggleIds[2])) slave[1] = 0;
                    if (
                        !selected.includes(toggleIds[1]) &&
                        !selected.includes(toggleIds[2]) &&
                        selected.includes(toggleIds[0])
                    ) {
                        master = [100, 0];
                    }
                    if (
                        selected.includes(toggleIds[1]) &&
                        !selected.includes(toggleIds[2])
                    ) {
                        slave[0] = 100;
                    } else if (
                        !selected.includes(toggleIds[1]) &&
                        selected.includes(toggleIds[2])
                    ) {
                        slave[1] = 100;
                    }
                    // document.getElementsByClassName(
                    //     "gutter-horizontal"
                    // )[0].style.display =
                    //     master[0] === 0 || master[0] === 100 ? "none" : "inline";
                    // document.getElementsByClassName(
                    //     "gutter-vertical"
                    // )[0].style.display =
                    //     slave[0] === 0 || slave[0] === 100 ? "none" : "block";

                    this.split1.setSizes(master);
                    //console.log(prevProps.selectedToggles);
                    this.dispatch({
                        persist: prevProps.selectedToggles != null, //Do not persist initial set of toggles
                        view:
                            viewName ||
                            /* istanbul ignore next */ this.props.viewName,
                        name: 0,
                        dimensions: master,
                    });
                    this.split2.setSizes(slave);
                    this.dispatch({
                        persist: prevProps.selectedToggles != null, //Do not persist initial set of toggles
                        view:
                            viewName ||
                            /* istanbul ignore next */ this.props.viewName,
                        name: 1,
                        dimensions: slave,
                    });
                }

                /* istanbul ignore next */
                if (!isEqual(prevProps.panes[0], this.props.panes[0])) {
                    this.split1.setSizes(this.props.panes[0]);
                }
                /* istanbul ignore next */
                if (!isEqual(prevProps.panes[1], this.props.panes[1])) {
                    this.split2.setSizes(this.props.panes[1]);
                }
            }
        }
        dispatch = ({
            persist,
            ...action
        }: {
            persist: boolean,
            view: string,
            name: number,
            dimensions: Array<number>,
        }) =>
            this.props.dispatch(
                persist
                    ? persistSplitPanesAction(action)
                    : setSplitPanesAction(action)
            );

        setSizes = (paneIdx: number, dimensions: Array<number>) => {
            if (paneIdx === 0) {
                this.split1.setSizes(dimensions);
            } else {
                /* istanbul ignore else */
                if (paneIdx === 1) {
                    this.split2.setSizes(dimensions);
                } else {
                    alert("Unknown paneIdx=" + paneIdx);
                }
            }
        };

        render() {
            const { panes, ...rest } = this.props;
            return <WrappedComponent {...rest} setSplitSizes={this.setSizes} />;
        }
    }

    const mapStateToProps = (state, ownProps) => ({
        selectedToggles: s.selectedTogglesSelector(
            state,
            viewName || /* istanbul ignore next */ ownProps.viewName
        ),
        panes: s.panesViewSelector(
            state,
            viewName || /* istanbul ignore next */ ownProps.viewName
        ),
        ...ownProps,
    });

    return connect(mapStateToProps)(PP);
};

export default SplitPanes;
